// 자동 퀘스트 진행 UI(시안 C7): 퀘스트 카드·던전 목표 패널의 「자동」 버튼과 Q 키로 시작하고,
// 왼쪽 가장자리 남색 금테 알약에 「자동 이동 중 · 목적지」와 멈춤(✕) 버튼을 보인다. 길찾기·판정은 autoPilot.ts.
// 시작 요청은 window 이벤트 AUTO_QUEST_EVENT(detail = QuestAction 또는 { kind: 'dungeon' })로 받는다.
import { autoActive, autoRoute, type AutoIcon } from '../shared/autoRoute';
import { cameraYaw } from '../shared/cameraYaw';
import { Btn, Scene, type GameCommand, type GameView, type InputSink } from '../shared/types';
import { AutoPilot, worldToScreen, type AutoEnd, type AutoOut, type AutoTarget } from './autoPilot';
import { FONT, el } from './dom';
import type { QuestAction } from './villageQuestModel';
import { viewRect, viewW, viewH } from '../shared/viewport';
import { autoStatusHudLayout, dungeonAutoButtonLayout } from './skillHudLayout';

export const AUTO_QUEST_EVENT = 'dh-auto-quest';
export type AutoRequest = QuestAction | { readonly kind: 'dungeon' } | { readonly kind: 'toggle' } | { readonly kind: 'stop' };

/** 마을 Q 키가 쓰는 지금 성장 목표의 동작(screens.ts가 목표가 바뀔 때 적는다) */
export const villageGoalAction: { action: QuestAction | null } = { action: null };

const END_TEXT: Partial<Record<AutoEnd, string>> = {
  manual: '직접 움직여서 자동 이동을 멈췄어요',
  stuck: '길을 찾지 못해 자동 이동을 멈췄어요',
  boss: '보스방에 도착했어요 · 자동 이동 끝',
  cleared: '모든 방을 정리했어요 · 자동 이동 끝',
};
const STEPS_SVG = '<svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true"><g fill="#ffd76a">' +
  '<path d="M9 4c3 0 4.5 3.2 4.2 6.8-.2 2.6-1.4 4.6-3.6 4.6S5.8 13.4 5.6 10.8C5.3 7.2 6 4 9 4Z"/><ellipse cx="9.6" cy="18.6" rx="3.2" ry="2.2"/>' +
  '<path d="M22 10c3 0 3.7 3.2 3.4 6.8-.2 2.6-1.8 4.6-4 4.6s-3.4-2-3.6-4.6C17.5 13.2 19 10 22 10Z"/><ellipse cx="21.4" cy="24.6" rx="3.2" ry="2.2"/></g></svg>';
/** 목표 아래 왼쪽 가장자리의 「자동」 알약. 디자인 배율로 터치 영역을 줄이지 않는다. */
const HUD_BTN = 'position:absolute;left:0;top:0;transform-origin:0 50%;pointer-events:auto;cursor:pointer;touch-action:manipulation;align-items:center;gap:5px;' +
  'width:88px;height:44px;box-sizing:border-box;padding:0 10px 0 12px;border:1.5px solid #e2b24a;border-radius:15px;background:linear-gradient(180deg,#2c3a68,#121a36);color:#ffe7a8;' +
  'font:800 14px ' + FONT + ';white-space:nowrap;box-shadow:0 2px 0 rgba(0,0,0,.45),0 3px 8px rgba(0,0,0,.4);';
const HUD_SHADOW_REST = '0 2px 0 rgba(0,0,0,.45),0 3px 8px rgba(0,0,0,.4)';
const HUD_SHADOW_GLOW = '0 0 12px rgba(255,226,140,.85),0 2px 0 rgba(0,0,0,.45)';
/** 번쩍임 줄이기: 맥동 없이 밝은 테두리·은은한 빛으로 고정 */
const HUD_CUE = 'border-color:#ffe9a6;box-shadow:0 0 8px rgba(255,226,140,.6),0 2px 0 rgba(0,0,0,.45);';
const HUD_ON = 'background:linear-gradient(180deg,#f7d57a,#c98c22);color:#2a1a04;box-shadow:0 0 10px rgba(242,193,78,.8),0 2px 0 rgba(0,0,0,.45);';

/**
 * 던전 「자동」 단추 글자: 방을 정리해 목표가 출구면 「출구로」, 다음 전투방으로 가야 하면 「다음 방」, 그 밖에는 「자동」.
 * 페르소나 QA(2026-11-03): 방을 다 깬 뒤 「자동 ▶」 뜻을 몰라 출구를 못 찾았다. 판정은 flow.objectiveLabel 글자(flow.ts clearRoom·방 이동 안내)로 한다.
 */
export function hudAutoLabel(objectiveLabel: string): string {
  if (objectiveLabel.includes('출구')) return '출구로';
  if (objectiveLabel.includes('전투방')) return '다음 방';
  return '자동';
}

/** 던전 「자동」 단추 접근성 이름: 상태(자동 진행 중이면 멈추기, 꺼져 있으면 글자별 행선지)에 맞춘다 */
export function hudAutoAriaLabel(label: string, running: boolean): string {
  if (running) return '자동 진행 중, 눌러서 멈추기 (Q)';
  if (label === '출구로') return '출구까지 자동 이동 (Q)';
  if (label === '다음 방') return '다음 전투방까지 자동 진행 (Q)';
  return '자동 진행 켜기 (Q)';
}

function keyOf(t: AutoTarget | null): string {
  return !t ? '' : t.kind === 'npc' ? 'npc:' + t.role : t.kind === 'door' ? 'door:' + t.region : t.kind === 'gate' ? 'gate' : 'dungeon';
}
function iconOf(t: AutoTarget | null): AutoIcon {
  // 보석상·연금술사(S7)는 상점 그림을 같이 쓴다
  return !t ? '' : t.kind === 'npc' ? (t.role === 'jeweler' || t.role === 'alchemist' ? 'shop' : t.role === 'raid' ? 'trainer' : t.role) : t.kind === 'door' || t.kind === 'gate' ? 'gate' : 'room';
}

export class AutoQuestUi {
  readonly pilot = new AutoPilot();
  private readonly pill: HTMLDivElement;
  private readonly statusIcon: HTMLSpanElement;
  private readonly text: HTMLSpanElement;
  private readonly stopBtn: HTMLButtonElement;
  private readonly hudBtn: HTMLButtonElement;
  private readonly hudBtnText: HTMLSpanElement;
  private kHud = -1;
  private kHudLabel = '';
  private kHudPulse = -1;
  private hudAnim: Animation | null = null;
  private kHudPos = '';
  private readonly out: AutoOut = { x: 0, z: 0, interact: false };
  private interactHeld = false;
  private kText = '';
  private kShow = false;
  private kPlaceKey = '';
  private kVersion = -1;
  private kCursor = -1;
  private noteUntil = 0;
  private note = '';

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly input: InputSink, private readonly send: (c: GameCommand) => void, private readonly touch: boolean) {
    this.pill = el('div', 'position:absolute;left:8px;display:none;align-items:center;gap:6px;pointer-events:none;box-sizing:border-box;' +
      'padding:6px 8px;border:2px solid #d9a441;border-radius:14px;background:linear-gradient(180deg,rgba(24,32,62,.95),rgba(11,16,34,.95));' +
      'box-shadow:inset 0 0 0 1px rgba(0,0,0,.65),inset 0 0 0 4px rgba(242,193,78,.14),0 4px 14px rgba(0,0,0,.55),0 0 14px rgba(242,193,78,.25);', parent);
    this.pill.dataset.testid = 'auto-status';
    // 양 끝 금색 마름모 장식
    for (const side of ['left', 'right']) el('div', 'position:absolute;top:50%;' + side + ':-7px;width:10px;height:10px;margin-top:-5px;transform:rotate(45deg);background:#0d1328;border:1.5px solid #f2c14e;', this.pill);
    this.statusIcon = el('span', 'display:flex;filter:drop-shadow(0 0 5px rgba(255,215,106,.7));', this.pill);
    this.statusIcon.innerHTML = STEPS_SVG;
    this.text = el('span', 'min-width:0;white-space:normal;word-break:keep-all;overflow-wrap:break-word;font:800 13px ' + FONT + ';line-height:1.2;color:#fff;letter-spacing:.2px;text-shadow:0 1px 0 #000,0 2px 4px rgba(0,0,0,.8);', this.pill, '');
    this.stopBtn = el('button', 'pointer-events:auto;cursor:pointer;box-sizing:border-box;width:44px;height:44px;display:grid;place-items:center;justify-self:end;padding:0;border-radius:50%;' +
      'border:2px solid #d9a441;background:linear-gradient(180deg,#2a3864,#121a34);color:#ffe7a8;font:900 16px ' + FONT + ';touch-action:manipulation;', this.pill, '✕') as HTMLButtonElement;
    this.stopBtn.type = 'button';
    this.stopBtn.dataset.testid = 'auto-stop';
    this.stopBtn.setAttribute('aria-label', '자동 이동 멈추기');
    this.stopBtn.addEventListener('pointerdown', (e) => e.preventDefault());
    this.stopBtn.addEventListener('click', () => this.request({ kind: 'stop' }));
    // 던전 「자동」: UI 맨 위 층(터치 조이스틱 입력 영역보다 위)에 두고, 자리는 HUD 목표 패널을 따라간다
    this.hudBtn = el('button', HUD_BTN + 'display:none;', parent) as HTMLButtonElement;
    this.hudBtn.type = 'button';
    this.hudBtn.dataset.testid = 'hud-auto';
    this.hudBtn.setAttribute('aria-label', '자동 진행 켜기 (Q)');
    this.hudBtn.title = '자동 진행 (Q)';
    this.hudBtnText = el('span', '', this.hudBtn, '자동');
    el('span', 'display:flex;', this.hudBtn).innerHTML = '<svg viewBox="0 0 10 12" width="9" height="11" aria-hidden="true"><path d="M1 1l8 5-8 5Z" fill="currentColor"/></svg>';
    this.hudBtn.addEventListener('pointerdown', (e) => e.preventDefault());
    this.hudBtn.addEventListener('keydown', (e) => { if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') e.stopPropagation(); });
    this.hudBtn.addEventListener('click', () => this.request({ kind: 'dungeon' }));
    window.addEventListener(AUTO_QUEST_EVENT, (e) => { const d = (e as CustomEvent<AutoRequest>).detail; if (d) this.request(d); });
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyQ' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.isContentEditable || t?.closest?.('input, textarea, select')) return;
      this.request({ kind: 'toggle' });
    });
  }

  /** 시작·전환·멈춤 요청 */
  request(r: AutoRequest): void {
    const v = this.view, sc = v.flow.scene;
    if (r.kind === 'stop') { this.pilot.stop('cancel'); return; }
    if (r.kind === 'toggle') {
      if (this.pilot.active) { this.pilot.stop('cancel'); return; }
      if (sc === Scene.Dungeon) r = { kind: 'dungeon' };
      else if (sc === Scene.Village && villageGoalAction.action) r = villageGoalAction.action;
      else return;
    }
    if (r.kind === 'bag') { this.pilot.stop('cancel'); if (sc === Scene.Village) this.send({ type: 'openInventory' }); return; }
    if (r.kind === 'dungeon' ? sc !== Scene.Dungeon : sc !== Scene.Village) return;
    if (v.flow.paused || v.village.menuNpc >= 0 || v.village.inventoryOpen || v.village.stageMapOpen || v.village.questOpen) return;
    const t: AutoTarget = r.kind === 'npc' ? { kind: 'npc', role: r.role } : r.kind === 'door' ? { kind: 'door', region: r.region } : r.kind === 'gate' ? { kind: 'gate' } : { kind: 'dungeon' };
    // 같은 목표를 다시 누르면 멈춘다
    if (keyOf(this.pilot.target) === keyOf(t)) { this.pilot.stop('cancel'); return; }
    this.pilot.start(t, v);
  }

  /** u: 디자인 배율. 렌더 프레임마다 1회 */
  update(u: number): void {
    const v = this.view, p = this.pilot, now = performance.now();
    const before = p.active;
    p.update(v, now, this.input.manualMove?.() ?? false, this.out);
    if (before && !p.active && p.ended && END_TEXT[p.ended]) { this.note = END_TEXT[p.ended]!; this.noteUntil = now + 2200; }
    // 이동: 월드 방향 → 화면 축(main.ts가 카메라 회전으로 다시 월드로 돌린다)
    if (this.out.travel) this.send({ type: 'travel', town: this.out.travel });
    worldToScreen(this.out, cameraYaw.rad);
    this.input.setAuto?.(this.out.x, this.out.z);
    // 상호작용: 한 프레임 눌렀다 다음 프레임에 뗀다
    if (this.interactHeld) { this.input.buttonUp(Btn.Interact); this.interactHeld = false; }
    if (this.out.interact) { this.input.buttonDown(Btn.Interact); this.interactHeld = true; }
    autoActive.key = keyOf(p.target);
    this.publishRoute();
    const sc = v.flow.scene;
    const live = (sc === Scene.Village || sc === Scene.Dungeon) && !v.flow.paused && v.village.menuNpc < 0 && !v.village.inventoryOpen && !v.village.stageMapOpen && !v.village.questOpen;
    this.updateHudButton(u, live && sc === Scene.Dungeon);
    // 알약
    let text = '';
    if (p.active) text = p.status === 'combat' ? '전투 중 · 방을 정리하면 다음 방으로 자동 이동' : '자동 이동 중 · ' + p.label(v);
    else if (now < this.noteUntil) text = this.note;
    const show = live && text !== '';
    if (show !== this.kShow) { this.kShow = show; this.pill.style.display = show ? 'grid' : 'none'; }
    if (!show) return;
    if (text !== this.kText) { this.kText = text; this.text.textContent = text; }
    this.stopBtn.style.display = p.active ? 'grid' : 'none';
    this.place();
  }

  /** 콤보 아래 왼쪽 띠. 긴 자동 이동·종료 이유도 생략하거나 축소하지 않고 줄바꿈한다. */
  private place(): void {
    const W = viewW(), H = viewH();
    const count = this.view.combo.count;
    const key = W + 'x' + H + ',' + this.view.flow.scene + ',' + (count > 0 ? String(count).length : 0) + ',' + this.pilot.active + ',' + this.kText.length;
    if (key === this.kPlaceKey) return;
    this.kPlaceKey = key;
    const combo = document.querySelector('[data-testid="hud-combo"]');
    // 숨은 콤보(크기 0)는 무시한다: 돌린 세로 화면에서 크기 0 사각형은 viewRect 아래 끝이 화면 높이(390)로 나와 알약이 화면 밖(y 398)으로 밀렸다(PR #211 화면 확인)
    const cr = combo ? viewRect(combo) : null;
    const bottom = cr && cr.width > 0 && cr.height > 0 ? cr.bottom : 0;
    // 던전에서는 목표 아래 「자동/진행 중」 버튼(updateHudButton과 같은 dungeonAutoButtonLayout)보다도 아래
    let autoBottom = 0;
    if (this.view.flow.scene === Scene.Dungeon) {
      const panel = document.querySelector('[data-testid="hud-objective"]');
      const r = panel ? viewRect(panel) : null;
      if (r && r.width) { const a = dungeonAutoButtonLayout(r.bottom); autoBottom = a.y + a.h; }
      else this.kPlaceKey = ''; // 목표 패널이 아직 자리 잡기 전이면 다음 프레임에 다시 잰다
    }
    // 폭·격자를 먼저 정해 실제 높이를 잰 뒤 화면 안 자리를 고른다(왼쪽 띠에 안 들어가면 상단 HUD 띠 아래 가운데, PR #211 리뷰 P2)
    const p = autoStatusHudLayout(W, bottom, autoBottom), active = this.pilot.active;
    this.pill.style.width = p.w + 'px';
    this.pill.style.gridTemplateColumns = p.stacked ? '26px minmax(0,1fr)' : '26px minmax(0,1fr)' + (active ? ' 44px' : '');
    this.text.style.gridColumn = p.stacked ? '1 / -1' : '2';
    this.text.style.gridRow = '1';
    this.stopBtn.style.gridColumn = p.stacked ? '2' : '3';
    this.stopBtn.style.gridRow = p.stacked ? '2' : '1';
    // 마름모 둘 다음의 발자국 아이콘. 좁은 폰에서는 텍스트 아래 멈춤 버튼과 같은 줄에 둔다.
    this.statusIcon.style.gridColumn = '1'; this.statusIcon.style.gridRow = p.stacked ? '2' : '1';
    const fit = autoStatusHudLayout(W, bottom, autoBottom, H, this.pill.offsetHeight || 0);
    this.pill.style.left = fit.x + 'px'; this.pill.style.top = fit.y + 'px';
  }

  /** 던전 목표 아래의 왼쪽 띠. 목표가 넓어도 자동 버튼은 중앙으로 밀려나지 않는다. */
  private updateHudButton(u: number, show: boolean): void {
    const state = !show ? 0 : autoActive.key === 'dungeon' ? 2 : 1;
    const posKey = show ? u.toFixed(3) + ',' + viewW() + 'x' + viewH() : '';
    const label = show ? hudAutoLabel(this.view.flow.objectiveLabel) : '자동';
    // 눈에 띄게 하기: 터치 기기에서 자동 진행이 꺼져 있고 「출구로/다음 방」일 때. 번쩍임 줄이기면 맥동 없이 테두리만 밝게
    const pulse = state === 1 && this.touch && label !== '자동' ? (this.view.settings.reduceFlash ? 2 : 1) : 0;
    if (state === this.kHud && posKey === this.kHudPos && label === this.kHudLabel && pulse === this.kHudPulse) return;
    let left = 0, top = 0;
    if (show) {
      const panel = document.querySelector('[data-testid="hud-objective"]');
      const r = panel ? viewRect(panel) : null;
      if (!r || !r.width) return;
      const p = dungeonAutoButtonLayout(r.bottom);
      left = p.x; top = p.y;
    }
    this.kHud = state; this.kHudPos = posKey; this.kHudLabel = label; this.kHudPulse = pulse;
    this.hudBtn.style.cssText = HUD_BTN + (state === 0 ? 'display:none;' : 'display:flex;') + (state === 2 ? HUD_ON : '') + (pulse === 2 ? HUD_CUE : '') +
      'left:' + left + 'px;top:' + top + 'px;';
    this.hudBtnText.textContent = state === 2 ? '진행 중' : label;
    this.hudBtn.setAttribute('aria-label', hudAutoAriaLabel(label, state === 2));
    // 맥동은 초당 1번 이하(주기 1.6초), 크기는 그대로(테두리·그림자 색만)
    this.hudAnim?.cancel(); this.hudAnim = null;
    if (pulse === 1 && typeof this.hudBtn.animate === 'function') {
      this.hudAnim = this.hudBtn.animate([
        { borderColor: '#e2b24a', boxShadow: HUD_SHADOW_REST },
        { borderColor: '#ffe9a6', boxShadow: HUD_SHADOW_GLOW },
        { borderColor: '#e2b24a', boxShadow: HUD_SHADOW_REST },
      ], { duration: 1600, iterations: Infinity, easing: 'ease-in-out' });
    }
  }

  /** 렌더 점선용 경로(지금 향하는 점부터)와 목적지 표식 */
  private readonly goal = { x: 0, z: 0 };
  private publishRoute(): void {
    const p = this.pilot, nav = p.nav, r = autoRoute;
    const walking = p.active && p.status === 'walking' && nav.count > 0;
    if (p.routeVersion === this.kVersion && p.cursor === this.kCursor && walking === r.active) return;
    this.kVersion = p.routeVersion; this.kCursor = p.cursor;
    r.active = walking;
    r.count = 0;
    if (walking) {
      for (let i = p.cursor; i < nav.count; i++) { r.xs[r.count] = nav.xs[i]; r.zs[r.count] = nav.zs[i]; r.count++; }
      if (p.goalPoint(this.view, this.goal)) { r.tx = this.goal.x; r.tz = this.goal.z; }
      else { r.tx = nav.xs[nav.count - 1]; r.tz = nav.zs[nav.count - 1]; }
    }
    r.icon = walking ? iconOf(p.target) : '';
    r.version++;
  }
}

